import { useState, useRef, useEffect, useMemo } from 'react';
import styles from './AuthCharacters.module.css';

/**
 * 瞳孔组件
 */
const Pupil = ({
  size = 12,
  maxDistance = 4,
  pupilColor = '#2D2D2D',
  forceLookX,
  forceLookY,
  mouseX,
  mouseY,
  parentRef
}) => {
  const position = useMemo(() => {
    if (!parentRef?.current) return { x: 0, y: 0 };

    if (forceLookX !== undefined && forceLookY !== undefined) {
      return { x: forceLookX, y: forceLookY };
    }

    const rect = parentRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const deltaX = mouseX - centerX;
    const deltaY = mouseY - centerY;
    const rawDistance = Math.sqrt(deltaX ** 2 + deltaY ** 2);
    const distance = Math.min(rawDistance, maxDistance);
    const angle = Math.atan2(deltaY, deltaX);

    return {
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance,
    };
  }, [mouseX, mouseY, forceLookX, forceLookY, maxDistance, parentRef]);

  return (
    <div
      className={styles.pupil}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: pupilColor,
        transform: `translate(${position.x}px, ${position.y}px)`,
      }}
    />
  );
};

/**
 * 眼白组件
 */
const EyeBall = ({
  size = 48,
  pupilSize = 16,
  maxDistance = 8,
  isBlinking = false,
  forceLookX,
  forceLookY,
  mouseX,
  mouseY,
  parentRef
}) => {
  return (
    <div
      ref={parentRef}
      className={styles.eyeBall}
      style={{
        width: `${size}px`,
        height: isBlinking ? '2px' : `${size}px`,
        backgroundColor: 'white',
      }}
    >
      {!isBlinking && (
        <Pupil
          size={pupilSize}
          maxDistance={maxDistance}
          forceLookX={forceLookX}
          forceLookY={forceLookY}
          mouseX={mouseX}
          mouseY={mouseY}
          parentRef={parentRef}
        />
      )}
    </div>
  );
};

function AuthCharacters({ currentEmail, currentPassword, showPassword }) {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const mouseX = mousePosition.x;
  const mouseY = mousePosition.y;

  const [isTyping, setIsTyping] = useState(false);
  const [isPurpleBlinking, setIsPurpleBlinking] = useState(false);
  const [isBlackBlinking, setIsBlackBlinking] = useState(false);
  const [isLookingAtEachOther, setIsLookingAtEachOther] = useState(false);
  const [isPurplePeeking, setIsPurplePeeking] = useState(false);
  const [mouthShape, setMouthShape] = useState('circle');
  const [purpleMouthShape, setPurpleMouthShape] = useState('circle');
  const [hasEntered, setHasEntered] = useState(false);

  const purpleRef = useRef(null);
  const blackRef = useRef(null);
  const yellowRef = useRef(null);
  const orangeRef = useRef(null);
  const eyePurpleLeftRef = useRef(null);
  const eyePurpleRightRef = useRef(null);
  const eyeBlackLeftRef = useRef(null);
  const eyeBlackRightRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setHasEntered(true);
    }, 60);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let rafId = null;

    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;

      if (rafId) cancelAnimationFrame(rafId);

      rafId = requestAnimationFrame(() => {
        setMousePosition({ x: clientX, y: clientY });
      });
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    if (!orangeRef.current) return;
    const rect = orangeRef.current.getBoundingClientRect();
    const isNearOrange =
      mouseX > rect.left - 50 &&
      mouseX < rect.right + 50 &&
      mouseY > rect.top - 50 &&
      mouseY < rect.bottom + 50;

    if (isNearOrange) {
      setMouthShape('circle');
    } else if (showPassword) {
      setMouthShape('foldUp');
    } else if (currentPassword.length > 0 && !showPassword) {
      setMouthShape('foldDown');
    } else {
      setMouthShape('circle');
    }
  }, [mouseX, mouseY, showPassword, currentPassword]);

  useEffect(() => {
    if (isTyping) {
      setPurpleMouthShape('foldDown');
    } else if (currentPassword.length > 0 && showPassword) {
      setPurpleMouthShape('foldUp');
    } else {
      setPurpleMouthShape('circle');
    }
  }, [isTyping, currentPassword, showPassword]);

  useEffect(() => {
    const createBlinker = (setState) => {
      let timeoutId;

      const schedule = () => {
        timeoutId = setTimeout(() => {
          setState(true);

          setTimeout(() => {
            setState(false);
            schedule();
          }, 150);
        }, Math.random() * 4000 + 3000);
      };

      schedule();
      return () => clearTimeout(timeoutId);
    };

    const cleanupPurple = createBlinker(setIsPurpleBlinking);
    const cleanupBlack = createBlinker(setIsBlackBlinking);

    return () => {
      cleanupPurple();
      cleanupBlack();
    };
  }, []);

  useEffect(() => {
    if (isTyping) {
      setIsLookingAtEachOther(true);
      const timer = setTimeout(() => setIsLookingAtEachOther(false), 800);
      return () => clearTimeout(timer);
    }

    setIsLookingAtEachOther(false);
  }, [isTyping]);

  useEffect(() => {
    if (currentPassword.length > 0 && showPassword) {
      const timer = setTimeout(() => {
        setIsPurplePeeking(true);
        setTimeout(() => setIsPurplePeeking(false), 800);
      }, Math.random() * 3000 + 2000);

      return () => clearTimeout(timer);
    }

    setIsPurplePeeking(false);
  }, [currentPassword, showPassword]);

  useEffect(() => {
    setIsTyping(currentEmail.length > 0 || currentPassword.length > 0);
  }, [currentEmail, currentPassword]);

  const calculatePosition = (ref) => {
    if (!ref?.current) return { faceX: 0, faceY: 0, bodySkew: 0 };

    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 3;
    const deltaX = mouseX - centerX;
    const deltaY = mouseY - centerY;

    return {
      faceX: Math.max(-10, Math.min(10, deltaX / 28)),
      faceY: Math.max(-8, Math.min(8, deltaY / 38)),
      bodySkew: Math.max(-4, Math.min(4, -deltaX / 180)),
    };
  };

  const purplePos = calculatePosition(purpleRef);
  const blackPos = calculatePosition(blackRef);
  const yellowPos = calculatePosition(yellowRef);
  const orangePos = calculatePosition(orangeRef);

  const isHidingPassword = currentPassword.length > 0 && !showPassword;

  const getPurpleStyle = () => ({
    height: isTyping || isHidingPassword ? '440px' : '400px',
    transform:
      currentPassword.length > 0 && !showPassword
        ? `skewX(0deg)`
        : isTyping || isHidingPassword
          ? `skewX(${purplePos.bodySkew - 8}deg) translateX(28px)`
          : `skewX(${purplePos.bodySkew}deg)`,
  });

  const getBlackStyle = () => ({
    transform:
      currentPassword.length > 0 && !showPassword
        ? `skewX(0deg)`
        : isLookingAtEachOther
          ? `skewX(${blackPos.bodySkew * 1.3 + 8}deg) translateX(12px)`
          : isTyping || isHidingPassword
            ? `skewX(${blackPos.bodySkew * 1.2}deg)`
            : `skewX(${blackPos.bodySkew}deg)`,
  });

  const getOrangeStyle = () => ({
    transform:
      currentPassword.length > 0 && !showPassword
        ? `skewX(0deg)`
        : `skewX(${orangePos.bodySkew}deg)`,
  });

  const getYellowStyle = () => ({
    transform:
      currentPassword.length > 0 && !showPassword
        ? `skewX(0deg)`
        : `skewX(${yellowPos.bodySkew}deg)`,
  });

  const purpleForce =
    currentPassword.length > 0 && showPassword
      ? { x: isPurplePeeking ? 3 : -3, y: isPurplePeeking ? 4 : -3 }
      : isLookingAtEachOther ? { x: 2, y: 3 } : {};

  const blackForce =
    currentPassword.length > 0 && showPassword
      ? { x: -3, y: -3 }
      : isLookingAtEachOther ? { x: 0, y: -3 } : {};

  const othersForce =
    currentPassword.length > 0 && showPassword ? { x: -4, y: -3 } : {};

  return (
    <div className={styles.charactersArea}>
      <div className={styles.charactersContainer}>
        <div
          ref={purpleRef}
          className={`${styles.purpleCharacter} ${hasEntered ? styles.enterPurple : styles.preEnterPurple}`}
          style={getPurpleStyle()}
          data-mouth={purpleMouthShape}
        >
          <div
            className={styles.purpleEyes}
            style={{
              left: currentPassword.length > 0 && showPassword
                ? '20px'
                : isLookingAtEachOther
                  ? '55px'
                  : `${45 + purplePos.faceX}px`,
              top: currentPassword.length > 0 && showPassword
                ? '35px'
                : isLookingAtEachOther
                  ? '65px'
                  : `${40 + purplePos.faceY}px`,
            }}
          >
            <EyeBall
              size={18}
              pupilSize={7}
              maxDistance={4}
              isBlinking={isPurpleBlinking}
              forceLookX={purpleForce.x}
              forceLookY={purpleForce.y}
              mouseX={mouseX}
              mouseY={mouseY}
              parentRef={eyePurpleLeftRef}
            />
            <EyeBall
              size={18}
              pupilSize={7}
              maxDistance={4}
              isBlinking={isPurpleBlinking}
              forceLookX={purpleForce.x}
              forceLookY={purpleForce.y}
              mouseX={mouseX}
              mouseY={mouseY}
              parentRef={eyePurpleRightRef}
            />
          </div>

          <div
            className={styles.purpleMouth}
            style={{
              left: currentPassword.length > 0 && showPassword
                ? '40px'
                : isLookingAtEachOther
                  ? '75px'
                  : `${70 + purplePos.faceX}px`,
              top: currentPassword.length > 0 && showPassword
                ? '90px'
                : isLookingAtEachOther
                  ? '100px'
                  : `${85 + purplePos.faceY}px`,
            }}
          >
            <div className={styles.purpleMouthContainer}>
              <div className={styles.purpleMouthShape}></div>
            </div>
          </div>
        </div>

        <div
          ref={blackRef}
          className={`${styles.blackCharacter} ${hasEntered ? styles.enterBlack : styles.preEnterBlack}`}
          style={getBlackStyle()}
        >
          <div
            className={styles.blackEyes}
            style={{
              left: currentPassword.length > 0 && showPassword
                ? '10px'
                : isLookingAtEachOther
                  ? '32px'
                  : `${26 + blackPos.faceX}px`,
              top: currentPassword.length > 0 && showPassword
                ? '28px'
                : isLookingAtEachOther
                  ? '12px'
                  : `${32 + blackPos.faceY}px`,
            }}
          >
            <EyeBall
              size={16}
              pupilSize={6}
              maxDistance={3}
              isBlinking={isBlackBlinking}
              forceLookX={blackForce.x}
              forceLookY={blackForce.y}
              mouseX={mouseX}
              mouseY={mouseY}
              parentRef={eyeBlackLeftRef}
            />
            <EyeBall
              size={16}
              pupilSize={6}
              maxDistance={3}
              isBlinking={isBlackBlinking}
              forceLookX={blackForce.x}
              forceLookY={blackForce.y}
              mouseX={mouseX}
              mouseY={mouseY}
              parentRef={eyeBlackRightRef}
            />
          </div>
        </div>

        <div
          ref={orangeRef}
          className={`${styles.orangeCharacter} ${hasEntered ? styles.enterOrange : styles.preEnterOrange}`}
          data-mouth={mouthShape}
          style={getOrangeStyle()}
        >
          <div
            className={styles.orangeEyes}
            style={{
              left: currentPassword.length > 0 && showPassword
                ? '50px'
                : `${82 + orangePos.faceX}px`,
              top: currentPassword.length > 0 && showPassword
                ? '85px'
                : `${90 + orangePos.faceY}px`,
            }}
          >
            <Pupil
              size={12}
              maxDistance={4}
              forceLookX={othersForce.x}
              forceLookY={othersForce.y}
              mouseX={mouseX}
              mouseY={mouseY}
              parentRef={orangeRef}
            />
            <Pupil
              size={12}
              maxDistance={4}
              forceLookX={othersForce.x}
              forceLookY={othersForce.y}
              mouseX={mouseX}
              mouseY={mouseY}
              parentRef={orangeRef}
            />
          </div>

          <div
            className={styles.orangeMouth}
            style={{
              left: currentPassword.length > 0 && showPassword
                ? '60px'
                : `${100 + orangePos.faceX}px`,
              top: currentPassword.length > 0 && showPassword
                ? '128px'
                : `${133 + orangePos.faceY}px`,
            }}
          >
            <div className={styles.mouthContainer}>
              <div className={styles.mouthShape}></div>
            </div>
          </div>
        </div>

        <div
          ref={yellowRef}
          className={`${styles.yellowCharacter} ${hasEntered ? styles.enterYellow : styles.preEnterYellow}`}
          style={getYellowStyle()}
        >
          <div
            className={styles.yellowEyes}
            style={{
              left: currentPassword.length > 0 && showPassword
                ? '20px'
                : `${52 + yellowPos.faceX}px`,
              top: currentPassword.length > 0 && showPassword
                ? '35px'
                : `${40 + yellowPos.faceY}px`,
            }}
          >
            <Pupil
              size={12}
              maxDistance={4}
              forceLookX={othersForce.x}
              forceLookY={othersForce.y}
              mouseX={mouseX}
              mouseY={mouseY}
              parentRef={yellowRef}
            />
            <Pupil
              size={12}
              maxDistance={4}
              forceLookX={othersForce.x}
              forceLookY={othersForce.y}
              mouseX={mouseX}
              mouseY={mouseY}
              parentRef={yellowRef}
            />
          </div>

          <div
            className={styles.yellowMouth}
            style={{
              left: currentPassword.length > 0 && showPassword
                ? '10px'
                : `${40 + yellowPos.faceX}px`,
              top: currentPassword.length > 0 && showPassword
                ? '88px'
                : `${88 + yellowPos.faceY}px`,
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default AuthCharacters;